import { Wallet, Users, HeartPulse, FolderLock, Activity, type LucideIcon } from "lucide-react";

export type FamilyHubNestedSectionId = "finances" | "people" | "life" | "vault" | "activity";

export type FamilyHubNestedItemId =
  | "wealth-insights"
  | "asset-map"
  | "goals"
  | "members"
  | "member-profiles"
  | "succession"
  | "preparedness"
  | "calendar"
  | "tasks"
  | "binder"
  | "notes"
  | "documents"
  | "templates"
  | "inventory"
  | "sharing"
  | "recent-activity"
  | "guides";

export interface FamilyHubNestedNavItem {
  id: FamilyHubNestedItemId;
  label: string;
}

export interface FamilyHubNestedNavGroup {
  section: FamilyHubNestedSectionId;
  label: string;
  icon: LucideIcon;
  items: FamilyHubNestedNavItem[];
}

export const FAMILY_HUB_NESTED_NAV: FamilyHubNestedNavGroup[] = [
  {
    section: "finances",
    label: "Finances",
    icon: Wallet,
    items: [
      { id: "wealth-insights", label: "Wealth insights" },
      { id: "asset-map", label: "Asset map" },
      { id: "goals", label: "Goals" },
    ],
  },
  {
    section: "people",
    label: "People",
    icon: Users,
    items: [
      { id: "members", label: "Members" },
      { id: "member-profiles", label: "Member profiles" },
      { id: "succession", label: "Succession & legacy" },
    ],
  },
  {
    section: "life",
    label: "Life admin",
    icon: HeartPulse,
    items: [
      { id: "preparedness", label: "Preparedness" },
      { id: "calendar", label: "Calendar" },
      { id: "tasks", label: "Tasks" },
    ],
  },
  {
    section: "vault",
    label: "Vault",
    icon: FolderLock,
    items: [
      { id: "binder", label: "Family binder" },
      { id: "notes", label: "Notes" },
      { id: "documents", label: "Documents" },
      { id: "templates", label: "Transfer templates" },
      { id: "inventory", label: "Asset inventory" },
      { id: "sharing", label: "Secure sharing" },
    ],
  },
  {
    section: "activity",
    label: "Activity",
    icon: Activity,
    items: [
      { id: "recent-activity", label: "Recent activity" },
      { id: "guides", label: "Guides & insights" },
    ],
  },
];

/** Maps legacy hub section ids (search, scroll targets) to sidebar nested routes. */
export const HUB_DESTINATION_NAV: Record<
  string,
  { section: FamilyHubNestedSectionId; item: FamilyHubNestedItemId }
> = {
  goals: { section: "finances", item: "goals" },
  wealth: { section: "finances", item: "wealth-insights" },
  members: { section: "people", item: "members" },
  calendar: { section: "life", item: "calendar" },
  tasks: { section: "life", item: "tasks" },
  notes: { section: "vault", item: "notes" },
  documents: { section: "vault", item: "documents" },
  binder: { section: "vault", item: "binder" },
  templates: { section: "vault", item: "templates" },
  inventory: { section: "vault", item: "inventory" },
};

const SECTION_IDS = new Set<FamilyHubNestedSectionId>(
  FAMILY_HUB_NESTED_NAV.map((group) => group.section)
);

const ITEM_IDS = new Set<FamilyHubNestedItemId>(
  FAMILY_HUB_NESTED_NAV.flatMap((group) => group.items.map((item) => item.id))
);

export function parseFamilyHubNestedSection(value: string | null): FamilyHubNestedSectionId | null {
  if (!value) return null;
  if (SECTION_IDS.has(value as FamilyHubNestedSectionId)) {
    return value as FamilyHubNestedSectionId;
  }
  return "finances";
}

/**
 * Parse Family Hub Nested Item.
 */
export function parseFamilyHubNestedItem(
  section: FamilyHubNestedSectionId | null,
  value: string | null
): FamilyHubNestedItemId | null {
  if (!section) return null;
  const group = FAMILY_HUB_NESTED_NAV.find((entry) => entry.section === section);
  if (value && ITEM_IDS.has(value as FamilyHubNestedItemId)) {
    const match = group?.items.find((item) => item.id === value);
    if (match) return match.id;
  }
  return group?.items[0]?.id ?? null;
}

export function findNestedNavLabel(
  section: FamilyHubNestedSectionId | null,
  item: FamilyHubNestedItemId | null
): string {
  if (!section || !item) return "Family Hub";
  const group = FAMILY_HUB_NESTED_NAV.find((entry) => entry.section === section);
  const navItem = group?.items.find((entry) => entry.id === item);
  return navItem?.label ?? group?.label ?? "Family Hub";
}
